import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Building2,
  CalendarCheck,
  CalendarDays,
  ClipboardList,
  FileSignature,
  Home,
  GraduationCap,
  Receipt,
  Inbox,
  LayoutGrid,
  ListChecks,
  MapPin,
  ShieldCheck,
  Settings as SettingsIcon,
  Shield,
  SlidersHorizontal,
  ShieldAlert,
  Users,
  Link2,
} from 'lucide-react'
import { cn, Dialog, DialogContent, DialogTitle } from '@biznomi/ui-react'
import { useAuth } from '@/lib/auth/store'
import { meetsPlan } from '@/lib/plan'
import { useOrg } from '@/lib/useOrg'
import { logout } from '@/lib/api/auth'
import { getAnomalyBadge } from '@/lib/api/badge'
import { getMyPendingContractCount } from '@/lib/api/contract'
import { getMyUnviewedPayslipCount } from '@/lib/api/payslip'

/**
 * 앱 전체의 단 하나의 셸.
 *
 * ★ 직원용/관리자용 화면을 나누지 않는다.
 *   사장이 출근을 찍고 바로 직원 근태를 보고 승인까지 하는 게 자연스러운 흐름인데,
 *   "관리자 화면"을 따로 두면 그게 다른 앱처럼 느껴진다. 게다가 그 화면이 PC 전용이면
 *   폰만 쓰는 소상공인은 관리 자체를 못 한다.
 *
 *   그래서 라우트도 메뉴도 하나로 두고, **껍데기만 화면 폭에 따라** 바꾼다.
 *     좁으면 하단탭 / 넓으면 좌측 사이드바 — 항목은 완전히 동일하다.
 *
 *   보이는 범위는 화면 크기가 아니라 **역할**이 정한다.
 */

interface NavItem {
  to: string
  label: string
  icon: typeof Home
  /** 최종관리자 전용 (§16-D15 보안 메뉴 등) */
  adminOnly?: boolean
  /** 관리자·부서장에게만 */
  staffOnly?: boolean
  /**
   * 이 기능이 켜진 조직에만 보인다 (PRO 게이트).
   * 서버도 requireFeature 로 막지만, 못 쓰는 메뉴를 보여주는 것 자체가 혼란이다.
   */
  feature?: 'leave' | 'stats' | 'contract' | 'payslip' | 'training'
  /**
   * 이 플랜 이상에서만 (서버도 requirePlan 으로 막는다).
   * features 와 따로 두는 이유 — features 는 조직이 끄는 스위치고,
   * plan 은 애초에 살 수 있느냐다. BASIC 조직에 메뉴를 띄워 놓고 403 을
   * 주면 "고장난 앱"으로 보인다.
   */
  minPlan?: 'PRO' | 'ENTERPRISE'
  /**
   * 「처리할 게 N건」 뱃지를 붙일 것.
   *
   * ⚠ **값을 여기 박지 않는다.** 이 배열은 정적 정의다. 키만 두고 값은 `AppShell` 이
   *   훅으로 읽는다 — 안 그러면 메뉴 정의가 서버 상태를 들고 있게 된다.
   */
  badge?: 'anomaly' | 'contract' | 'payslip'
  /** 하단탭에 올릴 것 — 폰에서는 4개까지가 한계다 */
  primary?: boolean
  end?: boolean
}

const SECTIONS: { title?: string; items: NavItem[] }[] = [
  {
    items: [
      { to: '/', label: '홈', icon: Home, primary: true, end: true },
      { to: '/records', label: '내 기록', icon: ListChecks, primary: true },
    ],
  },
  {
    /*
     * 직원 쪽도 나눈다. 관리자만 보다가 여기를 빠뜨렸는데, PRO 직원은 이 묶음이
     * 여섯 줄(홈·내 기록 + 서류 넷)이라 관리자 구역들보다 컸다.
     *
     * BASIC 은 넷 다 minPlan: 'PRO' 라 구역째 사라진다 → 직원은 홈·내 기록·설정 셋.
     *
     * 🔴 minPlan 이 아예 빠져 있었다 (2026-09-09). features 기본값이 leave:true 라
     *   BASIC 조직에서 휴가 메뉴 셋이 그대로 뜨고, 누르면 서버가 403 을 준다
     *   (leave.routes.ts 는 requireFeature + requirePlan('PRO') 둘 다 건다).
     *   prod 조직이 셋 다 PRO 라 한 번도 안 터졌다 — 첫 BASIC 체험 조직이 바로 만난다.
     *   가드레일 #5(check-plan-gate-parity.sh)가 이제 이걸 판다.
     */
    title: '내 서류',
    items: [
      { to: '/leave', label: '휴가', icon: CalendarDays, feature: 'leave', minPlan: 'PRO' },
      { to: '/contracts', label: '근로계약서', icon: FileSignature, feature: 'contract', minPlan: 'PRO', badge: 'contract' },
      { to: '/payslips', label: '급여명세서', icon: Receipt, feature: 'payslip', minPlan: 'PRO', badge: 'payslip' },
      { to: '/trainings', label: '교육 증빙', icon: GraduationCap, feature: 'training', minPlan: 'PRO' },
    ],
  },
  /*
   * 🔴 «관리» 한 덩이를 셋으로 갈랐다 (2026-09-09). 항목 수는 그대로다 —
   *   **한 덩이가 5개를 안 넘게** 하는 게 목적이다.
   *
   *   전   관리 한 덩이 · 보안        ← 현황 옆에 조직 설정이 있었다.
   *                                   매일 보는 것과 한 번 정하고 안 보는 것이 섞였다
   *   후   근태 · 직원 서류 · 조직 · 보안
   *
   *   BASIC 은 «직원 서류» 가 통째로 사라진다 (전부 minPlan: 'PRO'). 체험 조직은
   *   근태·조직·보안 셋만 본다.
   *
   * ⚠ 직원의 «내 서류» 와 이름이 겹치지 않게 «직원 서류» 로 둔다. 관리자는 둘을
   *   나란히 보고, 근로계약서·교육 증빙은 **양쪽에 다 있다** (내 것 / 남의 것).
   *
   * ⚠ «직원» 은 조직이 아니라 **근태** 에 둔다.
   *   부서장에게는 부서·근무지·조직 설정이 안 보여서, 직원을 조직에 두면
   *   **한 항목짜리 구역**이 생긴다 (제목만 있고 줄 하나 — 더 어수선하다).
   *   뜻으로도 근태가 맞다. 부서장이 직원 목록을 여는 건 조직을 짜려는 게 아니라
   *   사람을 찾아 기록을 보려는 것이고, 직원 상세 안에 기기·출퇴근·수기 이력이 다 있다.
   *
   * ⚠ 라우트는 하나도 안 바뀐다. 구역만 다시 묶었다.
   */
  {
    title: '근태',
    items: [
      { to: '/admin', label: '현황', icon: ClipboardList, staffOnly: true, primary: true, end: true },
      { to: '/admin/attendance', label: '출퇴근 기록', icon: ListChecks, staffOnly: true },
      { to: '/admin/approvals', label: '승인 대기', icon: Inbox, staffOnly: true },
      { to: '/admin/employees', label: '직원', icon: Users, staffOnly: true },
    ],
  },
  {
    title: '직원 서류',
    items: [
      { to: '/admin/leave-approvals', label: '휴가 승인', icon: CalendarCheck, staffOnly: true, feature: 'leave', minPlan: 'PRO' },
      { to: '/admin/leave-balances', label: '연차 부여', icon: CalendarDays, adminOnly: true, feature: 'leave', minPlan: 'PRO' },
      { to: '/admin/contracts', label: '근로계약서', icon: FileSignature, staffOnly: true, feature: 'contract', minPlan: 'PRO' },
      // 급여명세서는 부서장 제외 — 부서원 급여를 아는 것은 권한 남용으로 이어진다
      { to: '/admin/payslips', label: '급여명세서', icon: Receipt, adminOnly: true, feature: 'payslip', minPlan: 'PRO' },
      // 교육은 부서장도 본다 — 담당 부서만 보이고, 올리기·지우기는 ADMIN 뿐이다
      { to: '/admin/trainings', label: '교육 증빙', icon: GraduationCap, staffOnly: true, feature: 'training', minPlan: 'PRO' },
    ],
  },
  {
    title: '조직',
    items: [
      { to: '/admin/departments', label: '부서', icon: Building2, adminOnly: true },
      { to: '/admin/nfc-spots', label: '근무지', icon: MapPin, adminOnly: true },
      { to: '/admin/nfc-mappings', label: '담당 근무지', icon: ShieldCheck, adminOnly: true },
      { to: '/admin/settings', label: '조직 설정', icon: SlidersHorizontal, adminOnly: true },
    ],
  },
  {
    // 파는 물건 그 자체라 숨기지 않는다 — «기록이 분쟁 증거가 되는 조직» 이 타깃이다.
    // 자주 안 여는 것뿐이라 맨 아래에 둔다.
    title: '보안',
    items: [
      { to: '/admin/security/audit-logs', label: '감사 로그', icon: Shield, adminOnly: true },
      { to: '/admin/security/anomalies', label: '이상 탐지', icon: ShieldAlert, adminOnly: true, badge: 'anomaly' },
      { to: '/admin/security/chain-verify', label: '체인 검증', icon: Link2, adminOnly: true },
    ],
  },
  {
    items: [{ to: '/settings', label: '설정', icon: SettingsIcon, primary: true }],
  },
]

export function AppShell() {
  const navigate = useNavigate()
  const user = useAuth((s) => s.user)
  const clearAuth = useAuth((s) => s.clearAuth)
  const { data: org } = useOrg()

  const isAdmin = user?.role === 'ADMIN'
  const isStaff = isAdmin || user?.role === 'MANAGER'

  /*
   * 플랜·feature 게이트를 **한 번만** 정의한다. 메뉴를 그릴 때(`visible`)와 뱃지를 물을
   * 때(`enabled`)가 같은 규칙을 써야 한다 — 갈리면 «메뉴는 없는데 요청은 나가는» 상태가
   * 되고, 그 403 이 로그에 쌓여 다음 사람이 «정상» 으로 오해한다.
   */
  const canUse = (feature?: NavItem['feature'], minPlan?: NavItem['minPlan']) =>
    (!feature || !!org?.features?.[feature]) && (!minPlan || meetsPlan(org?.plan, minPlan))

  /*
   * 뱃지 값 — 메뉴 정의(`SECTIONS`)는 키만 들고 값은 여기서 읽는다.
   *
   * ⚠ `enabled: isAdmin` — 서버가 403 을 주는 엔드포인트다. 요청 자체를 안 낸다.
   * ⚠ `refetchOnWindowFocus` 를 여기서만 켠다. 전역 기본값은 false 이고, 뱃지는
   *   «창을 다시 볼 때» 신선하면 된다 (`lib/api/badge.ts` 주석 참조).
   */
  const anomalyQ = useQuery({
    queryKey: ['badge', 'anomaly'],
    enabled: isAdmin,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const res = await getAnomalyBadge()
      return res.success ? res.data?.count ?? 0 : 0
    },
  })
  /*
   * 🔴 이 둘은 축이 **둘**이다 — 플랜(PRO)과 feature. 서버 라우터가 둘 다 건다
   *   (`contract.routes.ts:49` · `payslip.routes.ts:43`). BASIC 조직 직원이 부르면 403 이다.
   *   그래서 `enabled` 를 메뉴가 보이는 조건과 똑같이 맞춘다.
   *
   * 🔴 **「내 서류」에만 붙는다.** 관리자에게는 「내 서류」와 「직원 서류」 양쪽에
   *   근로계약서·급여명세서가 있는데, 이 API 는 `getMy*` 다 — 본인 것만 센다.
   *   반대쪽에 붙이면 «직원들 것이 N건» 처럼 읽혀 거짓이 된다.
   */
  const contractQ = useQuery({
    queryKey: ['badge', 'contract'],
    enabled: canUse('contract', 'PRO'),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const res = await getMyPendingContractCount()
      return res.success ? res.data?.count ?? 0 : 0
    },
  })
  const payslipQ = useQuery({
    queryKey: ['badge', 'payslip'],
    enabled: canUse('payslip', 'PRO'),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const res = await getMyUnviewedPayslipCount()
      return res.success ? res.data?.count ?? 0 : 0
    },
  })

  /** 0 이면 안 그린다 — 「없음」을 굳이 보여줄 이유가 없고 뱃지가 늘 켜져 있으면 신호가 죽는다 */
  const badgeCount = (key: NavItem['badge']) =>
    (key === 'anomaly' ? anomalyQ.data : key === 'contract' ? contractQ.data : key === 'payslip' ? payslipQ.data : 0) ?? 0

  /*
   * 역할 + 기능 게이트. 못 쓰는 메뉴를 보여주면 눌렀다가 막히는 경험이 된다.
   * features 가 아직 안 왔을 때(로딩 중)는 감춘다 — 잠깐 떴다 사라지는 게 더 나쁘다.
   */
  const visible = (i: NavItem) => {
    if (i.adminOnly && !isAdmin) return false
    if (i.staffOnly && !isStaff) return false
    if (!canUse(i.feature, i.minPlan)) return false
    return true
  }
  const sections = SECTIONS.map((s) => ({ ...s, items: s.items.filter(visible) })).filter(
    (s) => s.items.length > 0,
  )

  /*
   * 폰 하단탭은 4개가 상한이다. 관리자에게 보이는 메뉴는 그보다 훨씬 많아서,
   * primary 만 탭에 올리면 나머지가 폰에서 **아예 열 수 없는 화면**이 된다.
   * PC 를 켜야 관리가 되면 이 셸을 하나로 둔 이유가 없어진다.
   *
   * ⚠ 개수를 세어 적지 말 것. 메뉴를 하나 늘릴 때마다 이 주석이 낡는다
   *   ("10개·나머지 7개" → 8/31 에 안 맞게 됐고, 고쳐 적은 "관리 11 + 보안 3" 도
   *    9/9 구역 분리로 또 안 맞게 됐다. 두 번 겪었으니 이제 안 적는다).
   *   아래 로직은 개수를 안 본다 — **탭에 못 올라간 게 남으면 시트를 띄운다.**
   *
   * 그래서 관리자에겐 4번째 탭을 "관리" 시트로 둔다. 사이드바와 같은 항목을 그대로
   * 담으므로 메뉴 정의는 여전히 SECTIONS 한 곳이다.
   *
   * ★ 같은 함정이 **직원에게도** 있다. 관리 메뉴가 없는 직원은 시트가 안 뜨는데,
   *   휴가·근로계약서는 primary 가 아니라 탭에도 없다 — 폰에서 여는 길이 사라진다.
   *   (PRO 화면을 만들어 놓고 정작 직원이 못 들어가는 상태였다.)
   *   그래서 탭에 못 올라간 항목이 남으면 역할과 무관하게 시트를 띄운다.
   */
  const allVisible = sections.flatMap((s) => s.items)
  const primary = allVisible.filter((i) => i.primary)
  const titledSections = sections.filter((s) => s.title)
  // 제목 없는 구역에서 탭에 못 올라간 것들 (직원의 휴가·근로계약서)
  const overflow = sections.filter((s) => !s.title).flatMap((s) => s.items).filter((i) => !i.primary)
  /*
   * 🔴 시트에서는 «본인 것» 을 뒤로 보낸다 (2026-09-09). **사이드바는 안 바꾼다.**
   *
   *   PRO 관리자가 폰에서 «관리» 를 누르면 맨 위가 «내 서류»(자기 휴가·급여명세서)였다.
   *   관리하러 연 시트인데 첫 줄이 본인 서류다. 그리고 시트가 넘쳐서
   *   **맨 아래 «보안» 세 항목이 화면 밖으로 잘렸다** — 감사 로그·이상 탐지·체인 검증은
   *   «기록이 분쟁 증거가 되는 조직» 에 파는 물건 그 자체다.
   *
   *   실측 (staging, trial-basic 을 잠깐 PRO 로): 링크 20 · 시트 675 · 내용 797 ·
   *   마지막 항목 하단 933 / 뷰포트 844.
   *
   *   2열 격자라 한 구역을 접으면 제목 1줄 + 2행 ≈ 130px 이 당겨진다. 부족한 게 122px 이라
   *   순서를 바꾸는 것만으로 보안이 화면 안에 들어온다. 스크롤 자체는 남지만 **접히는 것이
   *   «보안» 에서 «내 서류» 로 바뀐다.** 아래로 밀려야 할 건 본인 급여명세서지 감사 로그가 아니다.
   *
   * ⚠ PC 사이드바는 «홈·내 기록 → 내 서류» 가 맞다. `SECTIONS` 도 그대로 둔다 —
   *   여기서만 다시 묶는다. 메뉴 정의는 여전히 한 곳이다.
   * ⚠ 판별은 `sheetLabel` 과 같은 술어다 — staffOnly·adminOnly 가 하나도 없는 구역이
   *   «본인 것» 이다. 두 곳이 다른 기준을 쓰면 다음에 한쪽만 고쳐진다.
   */
  const isMine = (s: { items: NavItem[] }) => !s.items.some((i) => i.staffOnly || i.adminOnly)
  const sheetSections = [
    ...titledSections.filter((s) => !isMine(s)),
    ...(overflow.length > 0 ? [{ title: '내 메뉴', items: overflow }] : []),
    ...titledSections.filter(isMine),
  ]
  const hasSheet = sheetSections.length > 0
  /*
   * 🔴 «제목 있는 구역이 있으면 관리» 로 판단하면 안 된다 (2026-09-09).
   *   직원 쪽 «내 서류» 에 제목을 붙이는 순간 **직원 탭이 «관리» 로 바뀐다.**
   *   물어야 할 것은 «제목이 있나» 가 아니라 **«남의 것을 보는 메뉴가 있나»** 다.
   */
  const sheetLabel = allVisible.some((i) => i.staffOnly || i.adminOnly) ? '관리' : '더보기'
  const tabs = hasSheet ? primary.filter((i) => !i.staffOnly) : primary

  const [menuOpen, setMenuOpen] = useState(false)
  const { pathname } = useLocation()
  // 이동하면 시트는 닫는다 — 안 닫으면 새 화면이 시트에 가려진다
  useEffect(() => setMenuOpen(false), [pathname])

  const notice = org?.uiConfig?.noticeBanner

  const sheetActive = sheetSections.some((s) =>
    s.items.some((i) => (i.end ? pathname === i.to : pathname.startsWith(i.to))),
  )

  async function signOut() {
    await logout().catch(() => undefined)
    clearAuth()
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex min-h-dvh">
      {/* 넓은 화면 — 사이드바 */}
      <aside className="hidden w-56 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="border-b border-slate-100 px-5 py-4">
          <p className="truncate text-sm font-bold">{org?.name ?? '비즈노미'}</p>
          <p className="mt-0.5 truncate text-xs text-slate-400">
            {user?.name}
            {isStaff && ` · ${isAdmin ? '최종관리자' : '관리자'}`}
          </p>
        </div>

        <nav aria-label="주 메뉴" className="flex-1 overflow-y-auto px-3 py-4">
          {sections.map((section, idx) => (
            <div key={section.title ?? idx} className="mb-5">
              {section.title && (
                <p className="px-2 pb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                  {section.title}
                </p>
              )}
              <ul className="space-y-0.5">
                {section.items.map((i) => (
                  <li key={i.to}>
                    <NavLink
                      to={i.to}
                      end={i.end}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm',
                          isActive
                            ? 'bg-primary/10 font-semibold text-primary'
                            : 'text-slate-600 hover:bg-slate-50',
                        )
                      }
                    >
                      <i.icon className="size-4 shrink-0" />
                      {i.label}
                      <NavBadge n={badgeCount(i.badge)} />
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-slate-100 p-3">
          <button
            onClick={signOut}
            className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50"
          >
            로그아웃
          </button>
        </div>
      </aside>

      {/*
        🔴 아이폰 노치·다이나믹 아일랜드 (2026-09-09).

        iOS 셸이 `.ignoresSafeArea()` 로 WebView 를 노치 밑까지 그린다
        (`ios-native/ContentView.swift:54`). 하단 홈 인디케이터 구역을 흰 배경으로
        채워야 앱처럼 보이기 때문인데, **그래서 위쪽은 웹이 책임져야 한다.**
        하단은 짝이 있었고(아래 하단탭의 `paddingBottom`) 위쪽만 빠져 있었다.
        모바일에 상단바가 없어서 첫 줄(회사 이름·직원 이름)이 상태바에 가렸다.

        배경색을 같이 준다. 안 주면 인셋만큼 밀린 자리가 **투명**이라 노치 구역이 빈다.

        ⚠ 가로도 준다. 가로모드와 다이나믹 아일랜드에서 좌우 인셋이 0 이 아니다.
        ⚠ 안드로이드는 OS 가 먼저 밀어 준다(`fitsSystemWindows="true"`). 그때
          `env()` 는 0 이라 이 줄이 있어도 해가 없다.
        ⚠ `apps/client`(DCMC)에는 넣지 않는다. 같은 iOS 셸을 쓰지만 2,600명이 번들을
          받으므로 따로 판단한다 (기획 §1).

        🔴 시뮬레이터·Playwright 에서는 `env()` 가 0 이라 **고쳤는지 못 잰다.**
          실기기에서 값을 화면에 찍어 보는 것만이 근거다 [[feedback_make_the_device_talk]].
      */}
      <div
        className="flex min-w-0 flex-1 flex-col bg-[var(--color-bg)]"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        {/*
          조직 안내 문구 (§4 B #11). 마퀴로 흐르게 하지 않는다 — 움직이는 글씨는
          읽기 어렵고 접근성도 나쁘다. 한 줄로 고정해 두고, 길면 줄바꿈한다.
        */}
        {notice?.enabled && notice.text.trim() && (
          <div
            role="status"
            className="border-b border-[var(--color-warning)]/20 bg-[var(--color-warning-bg)] px-5 py-2 text-center text-sm text-[var(--color-warning)]"
          >
            {notice.text}
          </div>
        )}

        <main className="mx-auto w-full max-w-3xl flex-1 px-5 pb-28 pt-6 lg:max-w-none lg:pb-8 lg:pt-8">
          <Outlet />
        </main>

        {/* 좁은 화면 — 하단탭. 항목은 사이드바와 같은 것을 쓴다 */}
        <nav
          aria-label="하단 메뉴"
          className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white/95 backdrop-blur lg:hidden"
          /* 하단탭은 `fixed` 라 위 세로 열의 좌우 인셋을 안 받는다. 여기서 따로 준다 */
          style={{
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
          }}
        >
          <ul className="mx-auto flex max-w-md">
            {tabs.map((t) => (
              <li key={t.to} className="flex-1">
                <NavLink
                  to={t.to}
                  end={t.end}
                  className={({ isActive }) =>
                    cn(
                      'flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium',
                      isActive ? 'text-primary' : 'text-slate-400',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <t.icon className="size-6" strokeWidth={isActive ? 2.2 : 1.7} />
                      {t.label}
                    </>
                  )}
                </NavLink>
              </li>
            ))}

            {hasSheet && (
              <li className="flex-1">
                <button
                  type="button"
                  onClick={() => setMenuOpen(true)}
                  aria-haspopup="dialog"
                  aria-expanded={menuOpen}
                  className={cn(
                    'flex h-16 w-full flex-col items-center justify-center gap-1 text-[11px] font-medium',
                    sheetActive ? 'text-primary' : 'text-slate-400',
                  )}
                >
                  <LayoutGrid className="size-6" strokeWidth={sheetActive ? 2.2 : 1.7} />
                  {sheetLabel}
                </button>
              </li>
            )}
          </ul>
        </nav>

        {/* 시트 — 사이드바와 같은 항목. 폰에서 탭에 없는 화면에 닿는 유일한 통로다 */}
        <Dialog open={menuOpen} onOpenChange={setMenuOpen}>
          <DialogContent className="bottom-0 left-0 top-auto max-h-[80dvh] w-full max-w-none translate-x-0 translate-y-0 rounded-b-none pb-8 lg:hidden">
            <DialogTitle className="text-base">{sheetLabel}</DialogTitle>
            <div className="mt-3 space-y-4">
              {sheetSections.map((section, idx) => (
                <div key={section.title ?? idx}>
                  <p className="px-1 pb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    {section.title}
                  </p>
                  <ul className="grid grid-cols-2 gap-2">
                    {section.items.map((i) => (
                      <li key={i.to}>
                        <NavLink
                          to={i.to}
                          end={i.end}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-2.5 rounded-lg px-3 py-3 text-sm',
                              isActive
                                ? 'bg-primary/10 font-semibold text-primary'
                                : 'bg-slate-50 text-slate-600',
                            )
                          }
                        >
                          <i.icon className="size-4 shrink-0" />
                          {i.label}
                          <NavBadge n={badgeCount(i.badge)} />
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}

/**
 * 메뉴 뱃지 — 「처리할 게 N건」.
 *
 * ⚠ **0 이면 아무것도 그리지 않는다.** 늘 켜져 있는 뱃지는 신호가 아니라 장식이다.
 *   ⚠ 그래서 캡처로 증명하려면 **값이 1 이상인 상태**를 만들어야 한다 — 0 이라 안 보이는
 *     것과 아예 안 붙인 것은 그림이 같다.
 */
function NavBadge({ n }: { n: number }) {
  if (n <= 0) return null
  return (
    <span className="ml-auto min-w-5 rounded-full bg-[var(--color-danger)] px-1.5 text-center text-[11px] font-bold leading-5 text-white">
      {n > 99 ? '99+' : n}
    </span>
  )
}
